import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link as LinkIcon, Clock, Search, SendHorizontal, ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar';
import apinode from '../../services/apinode';
import swal from 'sweetalert';
import Lottie from 'lottie-react';
import '../pedido-editar/pedido-editar.css';
import animacaoLottie from './animations/animacao.json';
import productNotFoundLottie from './animations/productNotFound.json';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CustomSpinner } from '../../components/CustomSpinner';

const locale = ptBR;

/** === Design tokens === */
const T = {
  radius: 12,
  radiusLg: 16,
  shadowSm: '0 2px 10px rgba(0,0,0,.06)',
  shadowMd: '0 6px 18px rgba(0,0,0,.08)',
  border: '#e6eaef',
  bgApp: '#eef1f4',
  bgCard: '#ffffff',
  text: '#111b21',
  textSec: '#667781',
  primary: '#128C7E',
  primarySoft: '#e6f7f2',
};

/** Base de cartão reutilizável */
const cardBase = {
  background: T.bgCard,
  border: `1px solid ${T.border}`,
  borderRadius: T.radiusLg,
  boxShadow: T.shadowSm,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

/** === Styles === */
const S = {
  container: { padding: 0 },
  page: (isMobile, showingChat) => ({
    display: 'flex',
    height: 'calc(100vh - 64px)',
    background: T.bgApp,
    padding: isMobile ? 8 : 16,
    boxSizing: 'border-box',
    gap: isMobile ? 8 : 16,
    overflow: 'hidden',
  }),

  // Sidebar
  sidebarWrap: (isMobile, showingChat) => ({
    ...cardBase,
    width: 380,
    minWidth: 320,
    maxWidth: 440,
    display: isMobile ? (showingChat ? 'none' : 'flex') : 'flex',
  }),
  sidebarHeader: {
    background: '#f7fafc',
    borderBottom: `1px solid ${T.border}`,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  accountInfo: {
    textAlign: 'right',
    fontSize: 12,
    color: T.textSec,
    lineHeight: 1.25,
  },
  searchWrap: {
    padding: 12,
    borderBottom: `1px solid ${T.border}`,
    position: 'sticky',
    top: 53,
    background: '#ffffff',
    zIndex: 2,
  },
  searchInputWrap: { position: 'relative' },
  searchInput: {
    width: '100%',
    height: 42,
    padding: '0 14px 0 40px',
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    outline: 'none',
    fontSize: 14,
    background: '#fff',
    boxShadow: 'inset 0 1px 0 rgba(0,0,0,.02)',
  },
  searchIcon: { position: 'absolute', left: 12, top: 10, opacity: 0.6 },
  chatList: { overflowY: 'auto', padding: 8, display: 'grid', gap: 8 },

  chatItem: (active) => ({
    display: 'grid',
    gridTemplateColumns: '48px 1fr',
    gap: 12,
    padding: 10,
    borderRadius: 12,
    border: `1px solid ${active ? T.primary : T.border}`,
    background: active ? '#f2fffb' : '#fff',
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, border-color .12s ease',
    boxShadow: active ? T.shadowMd : T.shadowSm,
  }),
  thumb: {
    width: 48,
    height: 48,
    borderRadius: 10,
    objectFit: 'cover',
    boxShadow: '0 1px 2px rgba(0,0,0,.1)',
  },
  itemTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  itemTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: T.text,
    maxWidth: '70%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemTime: { fontSize: 12, color: T.textSec, whiteSpace: 'nowrap' },
  itemChips: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  chatBadge: {
    fontSize: 11,
    background: T.primarySoft,
    color: T.primary,
    padding: '3px 8px',
    borderRadius: 10,
    fontWeight: 700,
    border: `1px solid ${T.primary}`,
    whiteSpace: 'nowrap',
  },
  itemLastMsg: { fontSize: 13, color: T.textSec, lineHeight: 1.35 },

  // Chat
  chatPane: (isMobile, showingChat) => ({
    ...cardBase,
    flex: 1,
    display: isMobile ? (showingChat ? 'flex' : 'none') : 'flex',
  }),
  chatHeader: {
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    background: '#f7fafc',
    borderBottom: `1px solid ${T.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  backBtn: {
    border: `1px solid ${T.border}`,
    background: '#fff',
    padding: 6,
    borderRadius: 10,
    cursor: 'pointer',
    boxShadow: T.shadowSm,
  },
  chatHeaderThumb: {
    width: 44,
    height: 44,
    borderRadius: 10,
    objectFit: 'cover',
    boxShadow: '0 1px 2px rgba(0,0,0,.12)',
  },
  headerTitleWrap: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  chatHeaderTitle: {
    fontWeight: 800,
    color: T.text,
    fontSize: 15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chatHeaderSub: {
    fontSize: 12,
    color: T.textSec,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  infoChip: {
    fontSize: 11,
    background: '#fff',
    border: `1px solid ${T.border}`,
    color: '#3b4a54',
    padding: '3px 8px',
    borderRadius: 10,
    whiteSpace: 'nowrap',
  },
  linkBtn: {
    color: T.primary,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    textDecoration: 'none',
    fontSize: 12,
    fontWeight: 700,
  },

  messagesWrap: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    background: T.bgApp,
  },
  bubbleRow: (isMine) => ({
    display: 'flex',
    justifyContent: isMine ? 'flex-end' : 'flex-start',
  }),
  bubble: (isMine) => ({
    background: isMine ? '#e9fbe9' : '#fff',
    border: `1px solid ${T.border}`,
    boxShadow: T.shadowSm,
    padding: '10px 12px',
    borderRadius: 12,
    maxWidth: 620,
    lineHeight: 1.45,
    fontSize: 14.5,
    color: T.text,
  }),
  bubbleMeta: {
    fontSize: 11,
    color: T.textSec,
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    justifyContent: 'flex-end',
  },

  // Painel de produto (topo da conversa)
  productCard: {
    background: '#fff',
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    boxShadow: T.shadowSm,
    margin: '8px 12px 0',
    padding: 12,
  },
  productHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  productTitle: {
    fontWeight: 700,
    color: T.text,
    fontSize: 14,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '72%',
  },
  productSubGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 8,
    marginTop: 10,
  },
  productChip: {
    fontSize: 12,
    background: '#fafbfc',
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    padding: '6px 10px',
    color: T.text,
  },
  copyBtn: {
    border: `1px solid ${T.border}`,
    background: '#fff',
    padding: '6px 10px',
    borderRadius: 10,
    cursor: 'pointer',
    boxShadow: T.shadowSm,
    fontSize: 12,
    fontWeight: 600,
    color: T.text,
  },

  // Mini barra de produto (junto ao input)
  productMiniBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#f7fafc',
    borderTop: `1px solid ${T.border}`,
    padding: '8px 12px',
  },
  miniThumb: { width: 36, height: 36, borderRadius: 8, objectFit: 'cover' },
  miniTitle: {
    fontSize: 12.5,
    color: T.text,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 320,
  },

  // Input
  inputBar: {
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    borderTop: `1px solid ${T.border}`,
  },
  input: {
    flex: 1,
    border: `1px solid ${T.border}`,
    borderRadius: 999,
    padding: '12px 16px',
    outline: 'none',
    fontSize: 14,
    background: '#fff',
    boxShadow: 'inset 0 1px 0 rgba(0,0,0,.02)',
  },
  sendBtn: {
    border: 'none',
    background: T.primary,
    color: '#fff',
    borderRadius: 999,
    padding: '10px 14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: T.shadowSm,
  },
};

function Perguntas() {
  const [questions, setQuestions] = useState([]);
  const [questionByParam, setQuestionByParam] = useState(null);
  const [questionIsLoading, setQuestionIsLoading] = useState(true);
  const [selectedThreadKey, setSelectedThreadKey] = useState(null);
  const [response, setResponse] = useState('');
  const [localExtras, setLocalExtras] = useState(new Map());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showingChat, setShowingChat] = useState(false);

  // Produto (painel e mini barra)
  const [productDetails, setProductDetails] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productOpen, setProductOpen] = useState(true);

  const params = useParams();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  useEffect(() => { scrollToBottom(); }, [selectedThreadKey, localExtras, questions]);

  // Responsividade
  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth <= 768;
      setIsMobile(m);
      if (!m) setShowingChat(true);
      else setShowingChat(!!selectedThreadKey);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [selectedThreadKey]);

  const fetchData = useCallback(async () => {
    setQuestionIsLoading(true);
    try {
      const resp = await apinode.get(`/mercadolibreperguntas/${localStorage.idconta}`);

      if (params.idPergunta && resp.data.totais > 0) {
        resp.data.axiosResults.forEach((q) => {
          if (q.mercadoLibreData.total > 0) {
            const result = q.mercadoLibreData.questions.find((it) => String(it.id) === params.idPergunta);
            if (result) setQuestionByParam(result);
          }
        });
      }

      setQuestions(resp.data);
    } catch (error) {
      console.error('Erro ao buscar os dados:', error);
    } finally {
      setQuestionIsLoading(false);
    }
  }, [params.idPergunta]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Flatten + apelido
  const flatQuestions = useMemo(() => {
    if (!questions || !questions.axiosResults) return [];
    const all = [];
    questions.axiosResults.forEach((bucket) => {
      const contaApelido = bucket?.APELIDO || bucket?.apelido || null;
      const contaId = localStorage.idconta;
      if (bucket?.mercadoLibreData?.total > 0) {
        bucket.mercadoLibreData.questions.forEach((q) => {
          all.push({ ...q, contaApelido, contaId });
        });
      }
    });
    if (questionByParam && !all.some((q) => q.id === questionByParam.id)) {
      all.push({ ...questionByParam, contaApelido: null, contaId: localStorage.idconta });
    }
    return all.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
  }, [questions, questionByParam]);

  // Group by nickname
  const threads = useMemo(() => {
    const map = new Map();
    flatQuestions.forEach((q) => {
      const key = q?.userData?.nickname || 'Comprador';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    });
    map.forEach((arr) => arr.sort((a, b) => new Date(a.date_created) - new Date(b.date_created)));
    return map;
  }, [flatQuestions]);

  // Sidebar summaries
  const threadSummaries = useMemo(() => {
    const arr = [];
    threads.forEach((msgs, nickname) => {
      const last = msgs[msgs.length - 1];
      const first = msgs[0];
      arr.push({
        key: nickname,
        nickname,
        lastText: last?.text || '',
        lastTime: last?.date_created,
        thumb: first?.dadosItem?.body?.thumbnail || '',
        productTitle: first?.dadosItem?.body?.title || '',
        productPermalink: first?.dadosItem?.body?.permalink || '',
        contaApelido: first?.contaApelido || null,
        contaId: first?.contaId || localStorage.idconta,
      });
    });
    arr.sort((a, b) => new Date(b.lastTime || 0) - new Date(a.lastTime || 0));
    return arr;
  }, [threads]);

  // Seleção inicial
  useEffect(() => {
    if (!selectedThreadKey && threadSummaries.length > 0) {
      setSelectedThreadKey(threadSummaries[0].key);
    }
  }, [threadSummaries, selectedThreadKey]);

  useEffect(() => {
    if (isMobile) setShowingChat(!!selectedThreadKey);
    else setShowingChat(true);
  }, [isMobile, selectedThreadKey]);

  const selectedMessages = useMemo(() => {
    if (!selectedThreadKey) return [];
    return threads.get(selectedThreadKey) || [];
  }, [threads, selectedThreadKey]);

  // Mensagem local otimista
  const appendLocalMyMessage = (refQuestion, text) => {
    const myMsg = {
      id: `local-${Date.now()}`,
      text,
      date_created: new Date().toISOString(),
      isMine: true,
      dadosItem: refQuestion.dadosItem,
      userData: refQuestion.userData,
      seller_id: refQuestion.seller_id,
      apelido: refQuestion.apelido,
      contaApelido: refQuestion.contaApelido,
      contaId: refQuestion.contaId,
    };
    setLocalExtras((prev) => {
      const next = new Map(prev);
      const key = selectedThreadKey;
      const extras = next.get(key) ? [...next.get(key)] : [];
      extras.push(myMsg);
      next.set(key, extras);
      return next;
    });
  };

  const selectedMessagesWithExtras = useMemo(() => {
    const base = [...selectedMessages];
    const extras = localExtras.get(selectedThreadKey) || [];
    return [...base, ...extras].sort((a, b) => new Date(a.date_created) - new Date(b.date_created));
  }, [selectedMessages, localExtras, selectedThreadKey]);

  // Header info
  const headerInfo = useMemo(() => {
    const first = selectedMessages[0];
    if (!first) return null;
    const item = first.dadosItem?.body || {};
    return {
      nickname: selectedThreadKey,
      thumb: item.thumbnail || '',
      title: item.title || '',
      permalink: item.permalink || '#',
      price: item.price,
      listingType: item.listing_type_id,
      sold: item.sold_quantity,
      contaApelido: first.contaApelido,
      contaId: first.contaId,
    };
  }, [selectedMessages, selectedThreadKey]);

  // Merge util para produto
  const mergeProduct = (base, extra) => {
    const out = { ...(base || {}) };
    if (!out.title && extra && extra.title) out.title = extra.title;
    if (out.price == null && extra && extra.price != null) out.price = extra.price;
    if (!out.condition && extra && extra.condition) out.condition = extra.condition;
    if (out.available_quantity == null && extra && extra.available_quantity != null) out.available_quantity = extra.available_quantity;
    if (out.sold_quantity == null && extra && extra.sold_quantity != null) out.sold_quantity = extra.sold_quantity;
    if (!out.listing_type_id && extra && extra.listing_type_id) out.listing_type_id = extra.listing_type_id;
    if (!out.shipping && extra && extra.shipping) out.shipping = extra.shipping;
    if (!out.variations && extra && extra.variations) out.variations = extra.variations;
    if (!out.attributes && extra && extra.attributes) out.attributes = extra.attributes;
    if (!out.thumbnail && extra && extra.thumbnail) out.thumbnail = extra.thumbnail;
    if (!out.permalink && extra && extra.permalink) out.permalink = extra.permalink;
    return out;
  };

  // Buscar detalhes do produto
  const fetchProductDetails = useCallback(async (info) => {
    if (!info) { setProductDetails(null); return; }
    const base = info.dadosItem?.body || {};
    let merged = { ...base, permalink: base.permalink || info.permalink, title: base.title };
    const needsMore =
      merged && (
        merged.price == null ||
        !merged.condition ||
        merged.available_quantity == null ||
        !merged.attributes ||
        !merged.shipping
      );

    if (needsMore && merged.permalink) {
      try {
        setProductLoading(true);
        // Ajuste o endpoint conforme seu backend
        const res = await apinode.get(`/ml/itemByPermalink`, { params: { url: merged.permalink } });
        const extra = res.data?.item || res.data;
        merged = mergeProduct(merged, extra);
      } catch (e) {
        console.warn('Falha ao enriquecer produto por permalink:', e);
      } finally {
        setProductLoading(false);
      }
    }
    setProductDetails(merged);
  }, []);

  // Carregar produto quando o header mudar
  useEffect(() => {
    if (headerInfo) {
      fetchProductDetails({ dadosItem: { body: { ...headerInfo } }, permalink: headerInfo.permalink });
    } else {
      setProductDetails(null);
    }
  }, [headerInfo, fetchProductDetails]);

  // Enviar
  const [responseValue, setResponseValue] = useState('');
  useEffect(() => setResponseValue(response), [response]);

  const sendResponse = async () => {
    const last = selectedMessages[selectedMessages.length - 1];
    const textToSend = (responseValue || '').trim();
    if (!last || !textToSend) return;

    const payload = { text_resp: textToSend };
    appendLocalMyMessage(last, textToSend);
    setResponse('');

    try {
      await apinode.post(
        `/mercadolibreperguntasresposta/${last.id}/${last.seller_id}/${localStorage.idconta}`,
        payload
      );
      swal('Sucesso', 'Enviado ao Mercado Livre!', 'success');
      fetchData();
    } catch (error) {
      console.error(error);
      swal('Erro', 'Erro ao enviar resposta', 'warning');
      fetchData();
    }
  };

  return (
    <>
      <Navbar />
      <div className="container-fluid mt-page" style={S.container}>
        {questionIsLoading ? (
          <div className="mx-auto p-2" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <CustomSpinner />
          </div>
        ) : questions?.totais === 0 ? (
          <div className="row">
            <div
              className="centered-content"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '80vh' }}
            >
              <h4><p>Sem novas Perguntas</p></h4>
              <p>Clique na imagem pra voltar</p>
              <Link to="/">
                <Lottie animationData={animacaoLottie} style={{ maxWidth: 300, maxHeight: 300 }} />
              </Link>
            </div>
          </div>
        ) : (
          <div style={S.page(isMobile, showingChat)}>
            {/* Sidebar */}
            <aside style={S.sidebarWrap(isMobile, showingChat)}>
              <div style={S.sidebarHeader}>
                <div style={{ fontWeight: 800, color: T.text }}>Conversas</div>
                <div style={S.accountInfo}>
                  <div>Conta: <strong>{questions?.contaApelido || questions?.APELIDO || '-'}</strong></div>
                  <div>ID: {localStorage.idconta}</div>
                </div>
              </div>

              <div style={S.searchWrap}>
                <div style={S.searchInputWrap}>
                  <Search size={18} style={S.searchIcon} />
                  <input
                    placeholder="Buscar conversa, produto ou conta…"
                    style={S.searchInput}
                    onChange={(e) => {
                      const q = e.target.value.toLowerCase();
                      const nodes = document.querySelectorAll('[data-chat-item]');
                      nodes.forEach((n) => {
                        const txt = (n.getAttribute('data-title') || '').toLowerCase();
                        n.style.display = txt.includes(q) ? '' : 'none';
                      });
                    }}
                  />
                </div>
              </div>

              <div style={S.chatList}>
                {threadSummaries.map((t) => (
                  <div
                    key={t.key}
                    data-chat-item
                    data-title={`${t.nickname} ${t.productTitle} ${t.contaApelido || ''}`}
                    style={S.chatItem(selectedThreadKey === t.key)}
                    onClick={() => {
                      setSelectedThreadKey(t.key);
                      if (isMobile) setShowingChat(true);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                  >
                    <img src={t.thumb} alt={t.productTitle} style={S.thumb} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.itemTitleRow}>
                        <div style={S.itemTitle}>{t.nickname}</div>
                        <div style={S.itemTime}>
                          {t.lastTime ? `${formatDistanceToNow(new Date(t.lastTime), { locale })} atrás` : ''}
                        </div>
                      </div>
                      <div style={S.itemChips}>
                        <span style={S.chatBadge}>{t.contaApelido || 'Conta'}</span>
                      </div>
                      <div style={S.itemLastMsg}>
                        {t.lastText?.length > 72 ? t.lastText.slice(0, 72) + '…' : t.lastText}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </aside>

            {/* Chat */}
            <section style={S.chatPane(isMobile, showingChat)}>
              {/* Cabeçalho do chat */}
              {headerInfo ? (
                <div style={S.chatHeader}>
                  {isMobile && (
                    <button
                      style={S.backBtn}
                      onClick={() => setShowingChat(false)}
                      title="Voltar"
                    >
                      <ArrowLeft size={20} />
                    </button>
                  )}
                  <img src={headerInfo.thumb} alt={headerInfo.title} style={S.chatHeaderThumb} />
                  <div style={S.headerTitleWrap}>
                    <div style={S.chatHeaderTitle}>{headerInfo.nickname}</div>
                    <div style={S.chatHeaderSub}>
                      <span style={S.infoChip}>Conta: {headerInfo.contaApelido || '—'} (ID {headerInfo.contaId})</span>
                      {headerInfo.price != null && (
                        <span style={S.infoChip}>
                          {Number(headerInfo.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      )}
                      {headerInfo.listingType && <span style={S.infoChip}>{headerInfo.listingType}</span>}
                      {headerInfo.sold != null && <span style={S.infoChip}>{headerInfo.sold} vendas</span>}
                      <a href={headerInfo.permalink} target="_blank" rel="noreferrer" style={S.linkBtn} title="Ver anúncio">
                        <LinkIcon size={14} /> Ver anúncio
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={S.chatHeader} />
              )}

              {/* Painel de Dados do Anúncio */}
              {productDetails && (
                <div style={S.productCard}>
                  <div style={S.productHeaderRow}>
                    <div title={productDetails.title} style={S.productTitle}>
                      {productDetails.title || 'Anúncio'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {productDetails.price != null && (
                        <span style={S.productChip}>
                          {Number(productDetails.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      )}
                      <button
                        style={S.copyBtn}
                        onClick={() => {
                          if (productDetails.permalink) {
                            navigator.clipboard.writeText(productDetails.permalink);
                            swal('Link copiado', 'O link do anúncio foi copiado para a área de transferência.', 'success');
                          } else {
                            swal('Sem link', 'Não encontramos o permalink deste anúncio.', 'warning');
                          }
                        }}
                      >
                        Copiar link
                      </button>
                      <button
                        style={S.copyBtn}
                        onClick={() => setProductOpen((v) => !v)}
                      >
                        {productOpen ? 'Ocultar' : 'Mostrar'}
                      </button>
                    </div>
                  </div>

                  {productOpen && (
                    <>
                      <div style={S.productSubGrid}>
                        {productDetails.condition && (
                          <div style={S.productChip}>Condição: {productDetails.condition}</div>
                        )}
                        {productDetails.available_quantity != null && (
                          <div style={S.productChip}>Estoque: {productDetails.available_quantity}</div>
                        )}
                        {productDetails.sold_quantity != null && (
                          <div style={S.productChip}>Vendas: {productDetails.sold_quantity}</div>
                        )}
                        {productDetails.listing_type_id && (
                          <div style={S.productChip}>Tipo: {productDetails.listing_type_id}</div>
                        )}
                        {productDetails?.shipping?.mode && (
                          <div style={S.productChip}>Frete: {productDetails.shipping.mode}</div>
                        )}
                        {Array.isArray(productDetails?.variations) && (
                          <div style={S.productChip}>
                            Variações: {productDetails.variations.length}
                          </div>
                        )}
                      </div>

                      {Array.isArray(productDetails?.attributes) && productDetails.attributes.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 12, color: T.textSec, marginBottom: 6 }}>Atributos</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {productDetails.attributes.slice(0, 16).map((att, idx) => (
                              <span key={idx} style={S.productChip}>
                                {att?.name || att?.id}: <strong style={{ marginLeft: 4 }}>{att?.value_name || att?.value_id}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {productLoading && (
                    <div style={{ marginTop: 8, fontSize: 12, color: T.textSec }}>
                      Carregando informações do anúncio…
                    </div>
                  )}
                </div>
              )}

              {/* Mensagens */}
              <div id="messages-scroll" style={S.messagesWrap}>
                {selectedMessagesWithExtras.length === 0 ? (
                  <div
                    style={{
                      margin: 'auto',
                      textAlign: 'center',
                      color: T.textSec,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Lottie animationData={productNotFoundLottie} style={{ width: 220, height: 220 }} />
                    <div>Nenhuma mensagem nesta conversa ainda.</div>
                  </div>
                ) : (
                  <>
                    {selectedMessagesWithExtras.map((msg) => {
                      const isMine = msg.isMine === true;
                      const time = formatDistanceToNow(new Date(msg.date_created), { locale });
                      return (
                        <div key={msg.id} style={S.bubbleRow(isMine)}>
                          <div style={S.bubble(isMine)}>
                            {!isMine && (
                              <strong style={{ display: 'block', marginBottom: 4 }}>
                                {msg.userData?.nickname}
                              </strong>
                            )}
                            <div>{msg.text}</div>
                            <div style={S.bubbleMeta}>
                              <Clock size={12} />
                              <span>{time} atrás</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Mini barra do produto + Input */}
              <div style={S.productMiniBar}>
                {productDetails?.thumbnail && (
                  <img src={productDetails.thumbnail} alt="thumb" style={S.miniThumb} />
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={S.miniTitle} title={productDetails?.title}>
                    {productDetails?.title || '—'}
                  </div>
                  {productDetails?.price != null && (
                    <div style={{ fontSize: 12, color: T.textSec }}>
                      {Number(productDetails.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                  )}
                </div>
              </div>

              <div style={S.inputBar}>
                <input
                  style={S.input}
                  placeholder="Digite sua resposta…"
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendResponse();
                    }
                  }}
                />
                <button style={S.sendBtn} onClick={sendResponse} title="Enviar">
                  <SendHorizontal size={18} />
                  Enviar
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
}

export default Perguntas;
